
'use client'

import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowRight, ChevronDown } from 'lucide-react'
import Titlecontent from './Titlecontent'
import Grediantbutton from './Grediantbutton'

interface Solution {
  title: string
  description: string
  link: string
}

interface TailoredSolutionsProps {
  solutions?: Solution[]
}

export default function TailoredSolutions({
  solutions = [],
}: TailoredSolutionsProps) {
  const navigate = useNavigate()

  const [openIndex, setOpenIndex] = useState<number | null>(null)

  const handleToggle = (index: number) => {
    setOpenIndex((previousIndex) =>
      previousIndex === index ? null : index,
    )
  }

  const handleNavigation = (link: string) => {
    if (link) {
      navigate(link)
    }
  }

  // No solutions available
  if (solutions.length === 0) {
    return (
      <section className="tailored_solutions_sec py-5 md:py-10">
        <div className="container mx-auto px-6">
          <div className="title--head mb-6 text-center">
            <Titlecontent title="Tailored Solutions to Power Your Business" />
          </div>

          <div className="py-8 text-center">
            <p className="text-gray-500">
              No solutions available at the moment.
            </p>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section className="tailored_solutions_sec top-bottom bg-white">
      <div className="container mx-auto px-6">
        {/* Title */}
        <div className="text-center">
          <Titlecontent title="Tailored Solutions to Power Your Business" />
        </div>

        {/* =====================================================
            DESKTOP VIEW
            Visible from lg (1024px) and above
        ===================================================== */}
        <div className="mt-6 hidden lg:mt-12 lg:block">
          {solutions.map((solution, index) => (
            <div
              key={index}
              className="group relative flex items-center justify-between py-4 transition-all duration-500 ease-in-out"
              style={{
                borderWidth: '1px',
                borderStyle: 'solid',
                borderImageSource:
                  'linear-gradient(100.63deg, #2EC4F3 9.94%, #BFD633 86.86%)',
                borderImageSlice: 1,
                borderTop: 'none',
                borderLeft: 'none',
                borderRight: 'none',
              }}
            >
              <h3 className="relative text-[#000000] ml-6 w-1/2 font-semibold transition-colors duration-300 group-hover:text-[#2EC4F3] before:absolute before:left-[-20px] before:top-1/2 before:h-3 before:w-3 before:-translate-y-1/2 before:rounded-full before:bg-gradient-to-r before:from-[#2EC4F3] before:to-[#BFD633]">
                {solution.title}
              </h3>

              <p className="flex-1 text-[#000000] translate-y-2 opacity-0 transition-all duration-700 ease-in-out group-hover:translate-y-0 group-hover:opacity-100 md:w-[540px]">
                {solution.description}
              </p>

              <div className="relative">
                <div className="flex-1 translate-x-4 opacity-0 transition-all duration-500 ease-in-out group-hover:translate-x-0 group-hover:opacity-100">
                  <Link to={solution.link}>
                    <ArrowRight className="text-black transition-transform duration-300 group-hover:translate-x-2" />
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* =====================================================
            MOBILE + TABLET VIEW
            Visible below lg (1024px)
        ===================================================== */}
        <div className="block space-y-3 lg:hidden">
          {solutions.map((solution, index) => (
            <div
              key={index}
              className="overflow-hidden transition-all duration-300 ease-in-out hover:scale-[1.01]"
              style={{
                borderWidth: '1px',
                borderStyle: 'solid',
                borderImageSource:
                  'linear-gradient(100.63deg, #2EC4F3 9.94%, #BFD633 86.86%)',
                borderImageSlice: 1,
                borderTop: 'none',
                borderLeft: 'none',
                borderRight: 'none',
              }}
            >
              <button
                type="button"
                className="flex w-full items-center justify-between px-4 py-4 transition-all duration-300 ease-in-out hover:bg-gradient-to-r hover:from-[#2EC4F3]/5 hover:to-[#BFD633]/5"
                onClick={() => handleToggle(index)}
              >
                <div className="flex items-center space-x-3">
                  <div
                    className="h-4 w-4 rounded-full transition-all duration-300 ease-in-out"
                    style={{
                      background:
                        'linear-gradient(100.63deg, #2EC4F3 9.94%, #BFD633 86.86%)',
                    }}
                  />

                  <h3 className="text-left font-semibold transition-colors duration-300 hover:text-[#2EC4F3]">
                    {solution.title}
                  </h3>
                </div>

                <ChevronDown
                  className={`h-6 w-6 transform transition-all duration-500 ease-in-out ${
                    openIndex === index
                      ? 'rotate-180 text-[#2EC4F3]'
                      : 'text-gray-600'
                  }`}
                />
              </button>

              <div
                className={`overflow-hidden transition-all duration-500 ease-in-out ${
                  openIndex === index
                    ? 'max-h-96 opacity-100'
                    : 'max-h-0 opacity-0'
                }`}
              >
                <div
                  className={`px-4 pb-4 transition-all duration-700 ease-in-out ${
                    openIndex === index
                      ? 'translate-y-0 opacity-100'
                      : 'translate-y-4 opacity-0'
                  }`}
                  style={{
                    transitionDelay:
                      openIndex === index ? '200ms' : '0ms',
                  }}
                >
                  <p className="mb-3 text-start leading-relaxed text-gray-700">
                    {solution.description}
                  </p>

                  <button
                    type="button"
                    onClick={() => handleNavigation(solution.link)}
                    className="inline-flex items-center space-x-2 font-medium text-[#BFD633] transition-all duration-300 ease-in-out hover:scale-105 hover:text-[#2EC4F3]"
                  >
                    <span className="underline underline-offset-4">
                      Know More
                    </span>

                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Button */}
        <div className="mt-10 flex h-full items-center justify-center md:mt-12">
          <Grediantbutton
            btntext="View All Services"
            link="https://lemolite.com/services"
          />
        </div>
      </div>
    </section>
  )
}

